import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, blobErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AccessBlocked } from "@/components/AccessBlocked";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Download, Upload, FileSpreadsheet, Undo2, CheckCircle2 } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";

const STATUS_STYLES = {
  ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  duplicate_orbit: "bg-amber-50 text-amber-700 border-amber-200",
  duplicate_file: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
  agent_not_found: "bg-blue-50 text-blue-700 border-blue-200",
};

const saveBlob = (data, name) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  window.URL.revokeObjectURL(url);
};

export default function LeadImportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r)) || perms.includes("admin.full_access") || perms.includes("leads.admin");
  const canImport = isAdmin || perms.includes("leads.import");

  const fileRef = useRef(null);
  const [leadType, setLeadType] = useState("meta");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [agentFix, setAgentFix] = useState({});
  const [cityAccept, setCityAccept] = useState({});
  const [stateAccept, setStateAccept] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [blocked, setBlocked] = useState(null);

  const loadBatches = () => api.get("/lead-import/batches")
    .then(r => { setBatches(r.data || []); setBlocked(null); })
    .catch(e => { if ([401, 403, 410].includes(e.response?.status)) setBlocked(formatApiError(e.response?.data?.detail)); });
  useEffect(() => { loadBatches(); }, []);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/lead-import/template", { responseType: "blob" });
      saveBlob(r.data, "orbit_lead_upload_template.xlsx");
    } catch (e) { toast.error(await blobErrorMessage(e, "Template download failed")); }
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name); setResult(null); setPreview(null);
    setAgentFix({}); setCityAccept({}); setStateAccept({});
    const fd = new FormData();
    fd.append("file", f);
    fd.append("lead_type", leadType);
    setBusy(true);
    try {
      const r = await api.post("/lead-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not read this file");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const unmatchedAgents = [...new Set((preview?.rows || []).filter(r => r.status === "agent_not_found").map(r => r.agent_value))];
  const citySuggestions = [...new Set((preview?.rows || []).filter(r => r.city_suggestion).map(r => `${r.city}→${r.city_suggestion}`))];
  const stateSuggestions = [...new Set((preview?.rows || []).filter(r => r.state_suggestion).map(r => `${r.state}→${r.state_suggestion}`))];

  const importable = (preview?.rows || []).filter(r => ["ready", "agent_not_found"].includes(r.status)).length;

  const doImport = async () => {
    setBusy(true);
    try {
      const r = await api.post("/lead-import/commit", {
        draft_id: preview.draft_id,
        agent_overrides: agentFix,
        city_accepts: cityAccept,
        state_accepts: stateAccept,
      });
      setResult(r.data); setPreview(null);
      toast.success(`${r.data.counts.created} lead(s) imported`);
      loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };

  const downloadErrors = async (batchId) => {
    try {
      const r = await api.get(`/lead-import/batches/${batchId}/errors`, { responseType: "blob" });
      saveBlob(r.data, `lead_import_errors_${batchId}.xlsx`);
    } catch (e) { toast.error(await blobErrorMessage(e, "Download failed")); }
  };

  const undoBatch = async (batchId) => {
    if (!window.confirm("Remove the leads from this import that nobody has worked on yet?")) return;
    try {
      const r = await api.post(`/lead-import/batches/${batchId}/undo`, {});
      toast.success(`${r.data.removed} removed · ${r.data.kept_because_worked_on} kept (already worked on)`);
      loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Undo failed"); }
  };

  if (!canImport) return <div className="p-6"><AccessBlocked detail="leads.import required" testid="lead-import-blocked" /></div>;
  if (blocked) return <div className="p-6"><AccessBlocked detail={blocked} testid="lead-import-blocked" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="lead-import-page">
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="sm" onClick={() => navigate("/leads")} data-testid="back-to-desk"><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Upload leads</h1>
          <p className="text-xs text-zinc-400 mt-0.5">Excel or CSV · up to 2,000 rows · 5 MB</p>
        </div>
      </div>

      <div className="border border-zinc-200 rounded-sm p-4 mb-5 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-xs text-zinc-500">Lead type</Label>
            <Select value={leadType} onValueChange={setLeadType}>
              <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-lead-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="meta">Meta campaign</SelectItem>
                <SelectItem value="old_database">Old database / other</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-zinc-400 mt-1">Old-database leads carry no campaign data and stay out of the campaign report.</p>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Template</Label>
            <Button variant="outline" size="sm" className="h-8 text-xs mt-1 w-full" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Download template
            </Button>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">File</Label>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onPick} className="hidden" data-testid="file-input" />
            <Button size="sm" className="h-8 text-xs mt-1 w-full" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="pick-file-btn">
              <Upload className="w-3.5 h-3.5 mr-1" /> {busy ? "Reading..." : fileName || "Choose file"}
            </Button>
          </div>
        </div>
      </div>

      {result && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-4 mb-5" data-testid="import-result">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-emerald-800">Import finished</h3>
          </div>
          <p className="text-xs text-emerald-800" data-testid="result-counts">
            {result.counts.created} created · {result.counts.assigned} assigned to agents · {result.counts.unassigned} unassigned · {result.counts.skipped} skipped
          </p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => downloadErrors(result.batch_id)} data-testid="download-errors-btn">
              <Download className="w-3 h-3 mr-1" /> Download error report
            </Button>
            <Button size="sm" className="h-7 text-[11px]" onClick={() => navigate("/leads")} data-testid="go-to-desk-btn">Open Lead Desk</Button>
          </div>
        </div>
      )}

      {preview && (
        <div className="mb-6" data-testid="preview-section">
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="preview-counts">
            <Badge variant="outline" className="text-[11px] border-emerald-200 text-emerald-700" data-testid="count-ready">Ready {preview.counts.ready}</Badge>
            <Badge variant="outline" className="text-[11px] border-amber-200 text-amber-700" data-testid="count-duplicates">Duplicates {preview.counts.duplicates}</Badge>
            <Badge variant="outline" className="text-[11px] border-red-200 text-red-700" data-testid="count-errors">Errors {preview.counts.errors}</Badge>
            <Badge variant="outline" className="text-[11px] border-blue-200 text-blue-700" data-testid="count-unassigned">Unassigned {preview.counts.unassigned}</Badge>
            <Badge variant="outline" className="text-[11px] border-violet-200 text-violet-700" data-testid="count-cityfixes">City fixes {preview.counts.city_fixes}</Badge>
            {preview.extra_columns?.length > 0 && (
              <span className="text-[11px] text-zinc-500" data-testid="extra-columns">+ {preview.extra_columns.length} question column(s) → Internal Notes</span>
            )}
          </div>

          {unmatchedAgents.length > 0 && (
            <div className="border border-blue-200 bg-blue-50 rounded-sm p-3 mb-3" data-testid="agent-fix-panel">
              <h4 className="text-xs font-semibold text-blue-900 mb-2">Agents not found — choose one for every row carrying that name</h4>
              <div className="space-y-2">
                {unmatchedAgents.map(val => {
                  const suggestion = (preview.rows.find(r => r.agent_value === val) || {}).agent_suggestion;
                  return (
                    <div key={val} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`agent-fix-${val}`}>
                      <span className="font-mono text-blue-900">{val}</span>
                      {suggestion && <span className="text-blue-700">Did you mean {suggestion}?</span>}
                      <Select value={agentFix[val.toLowerCase()] || "unassigned"} onValueChange={v => setAgentFix(f => ({ ...f, [val.toLowerCase()]: v }))}>
                        <SelectTrigger className="h-7 text-[11px] w-56" data-testid={`agent-select-${val}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Leave unassigned</SelectItem>
                          {(preview.agents || []).map(a => <SelectItem key={a.user_id} value={a.user_id}>{a.name} ({a.email})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(citySuggestions.length > 0 || stateSuggestions.length > 0) && (
            <div className="border border-violet-200 bg-violet-50 rounded-sm p-3 mb-3" data-testid="city-fix-panel">
              <h4 className="text-xs font-semibold text-violet-900 mb-2">Spelling suggestions</h4>
              <div className="space-y-1">
                {citySuggestions.map(pair => {
                  const [typed, suggested] = pair.split("→");
                  const key = typed.toLowerCase();
                  return (
                    <div key={pair} className="flex items-center gap-2 text-xs" data-testid={`city-fix-${typed}`}>
                      <span className="text-violet-900">{typed} → {suggested}?</span>
                      <Button size="sm" variant={cityAccept[key] ? "default" : "outline"} className="h-6 text-[10px]" onClick={() => setCityAccept(c => ({ ...c, [key]: true }))} data-testid={`accept-city-${typed}`}>Accept</Button>
                      <Button size="sm" variant={cityAccept[key] ? "outline" : "default"} className="h-6 text-[10px]" onClick={() => setCityAccept(c => ({ ...c, [key]: false }))} data-testid={`keep-city-${typed}`}>Keep as typed</Button>
                    </div>
                  );
                })}
                {stateSuggestions.map(pair => {
                  const [typed, suggested] = pair.split("→");
                  const key = typed.toLowerCase();
                  return (
                    <div key={pair} className="flex items-center gap-2 text-xs" data-testid={`state-fix-${typed}`}>
                      <span className="text-violet-900">{typed} → {suggested}?</span>
                      <Button size="sm" variant={stateAccept[key] ? "default" : "outline"} className="h-6 text-[10px]" onClick={() => setStateAccept(c => ({ ...c, [key]: true }))} data-testid={`accept-state-${typed}`}>Accept</Button>
                      <Button size="sm" variant={stateAccept[key] ? "outline" : "default"} className="h-6 text-[10px]" onClick={() => setStateAccept(c => ({ ...c, [key]: false }))} data-testid={`keep-state-${typed}`}>Keep as typed</Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border border-zinc-200 rounded-sm overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200">
                {["Row", "Status", "Name", "Phone", "Email", "Company", "Pincode", "City", "State", "Agent", "Campaign"].map(h => (
                  <th key={h} className="text-left px-2 py-1.5 font-medium text-zinc-600">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {preview.rows.map(r => (
                  <tr key={r.row_no} className="border-b border-zinc-100" data-testid={`preview-row-${r.row_no}`}>
                    <td className="px-2 py-1.5 text-zinc-400">{r.row_no}</td>
                    <td className="px-2 py-1.5">
                      <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[r.status] || ""}`} data-testid={`row-status-${r.row_no}`}>
                        {r.status === "error" ? `Error: ${r.error}` : preview.status_labels[r.status]}
                      </Badge>
                      {r.status === "duplicate_orbit" && r.existing_lead_id && (
                        <button className="ml-1 underline text-blue-600" onClick={() => navigate(`/leads/${r.existing_lead_id}`)} data-testid={`open-existing-${r.row_no}`}>open it</button>
                      )}
                      {r.status === "duplicate_file" && <span className="ml-1 text-zinc-400">{r.error}</span>}
                      {r.status === "agent_not_found" && r.agent_suggestion && <span className="ml-1 text-blue-700">Did you mean {r.agent_suggestion}?</span>}
                    </td>
                    <td className="px-2 py-1.5 font-medium text-zinc-800">{r.name || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-600 font-mono">{r.phone || r.phone_as_typed || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{r.email || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{r.company || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-600 font-mono">{r.pincode || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-600">
                      {r.city || "—"}
                      {r.district && <span className="block text-[9px] text-zinc-400">District: {r.district}</span>}
                      {r.city_suggestion && <span className="block text-[9px] text-violet-600">{r.city} → {r.city_suggestion}?</span>}
                    </td>
                    <td className="px-2 py-1.5 text-zinc-600">{r.state || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{r.agent_name || r.agent_value || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-500">{r.campaign || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => setPreview(null)} data-testid="cancel-import-btn">Cancel</Button>
            <Button size="sm" onClick={doImport} disabled={busy || importable === 0} data-testid="confirm-import-btn">
              {busy ? "Importing..." : `Import ${importable} leads`}
            </Button>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-zinc-900 mb-2">Import history</h3>
        {batches.length === 0 ? (
          <p className="text-center text-zinc-400 text-xs py-6 border border-dashed border-zinc-200 rounded-sm">No uploads yet.</p>
        ) : (
          <div className="border border-zinc-200 rounded-sm overflow-x-auto" data-testid="import-history">
            <table className="w-full text-[11px]">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200">
                {["File", "Type", "By", "When", "Created", "Skipped", ""].map(h => <th key={h} className="text-left px-2 py-1.5 font-medium text-zinc-600">{h}</th>)}
              </tr></thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b._id} className="border-b border-zinc-100" data-testid={`batch-${b._id}`}>
                    <td className="px-2 py-1.5 text-zinc-800"><FileSpreadsheet className="w-3 h-3 inline mr-1 text-zinc-400" />{b.file_name}</td>
                    <td className="px-2 py-1.5 text-zinc-500">{b.lead_type === "old_database" ? "Old database" : "Meta campaign"}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{b.imported_by_name}</td>
                    <td className="px-2 py-1.5 text-zinc-500">{fmtDateTime(b.created_at)}</td>
                    <td className="px-2 py-1.5 text-zinc-800">{b.counts?.created ?? 0}</td>
                    <td className="px-2 py-1.5 text-zinc-500">{b.counts?.skipped ?? 0}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => downloadErrors(b._id)} data-testid={`batch-errors-${b._id}`}>Errors</Button>
                      {isAdmin && b.undoable && (
                        <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-600" onClick={() => undoBatch(b._id)} data-testid={`batch-undo-${b._id}`}>
                          <Undo2 className="w-3 h-3 mr-0.5" /> Undo
                        </Button>
                      )}
                      {b.undone && <span className="text-[10px] text-zinc-400" data-testid={`batch-undone-${b._id}`}>undone</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
